import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import { businessApi, categoriesApi } from '../services/api';
import { toast } from 'sonner';
import { Building2, Upload, X, Plus, Star } from 'lucide-react';

const SubmitBusiness = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isBusinessOwner, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [ownershipTags, setOwnershipTags] = useState([]);
  const [existingBusiness, setExistingBusiness] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [photosPreviews, setPhotosPreviews] = useState([]);

  const [form, setForm] = useState({
    business_name: '',
    primary_category: '',
    secondary_category: '',
    subcategory: '',
    description: '',
    street_address: '',
    city_state_zip: '',
    phone: '',
    website: '',
    contact_email: '',
    facebook: '',
    instagram: '',
    twitter: '',
    ownership_tags: [],
    hours: {}
  });

  const [subcategories, setSubcategories] = useState([]);
  const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || !isBusinessOwner) {
      navigate('/business-login');
      return;
    }
    loadData();
  }, [isAuthenticated, isBusinessOwner, authLoading, navigate]);

  const loadData = async () => {
    try {
      const [catsRes, tagsRes, bizRes] = await Promise.all([
        categoriesApi.getBusinessCategories(),
        categoriesApi.getOwnershipTags(),
        businessApi.getMine().catch(() => ({ data: null }))
      ]);
      setCategories(catsRes.data);
      setOwnershipTags(tagsRes.data);
      
      if (bizRes.data) {
        setExistingBusiness(bizRes.data);
        const existingCat = bizRes.data.primary_category || '';
        setForm({
          business_name: bizRes.data.business_name || '',
          primary_category: existingCat,
          secondary_category: bizRes.data.secondary_category || '',
          subcategory: bizRes.data.subcategory || '',
          description: bizRes.data.description || '',
          street_address: bizRes.data.street_address || '',
          city_state_zip: bizRes.data.city_state_zip || '',
          phone: bizRes.data.phone || '',
          website: bizRes.data.website || '',
          contact_email: bizRes.data.contact_email || '',
          facebook: bizRes.data.facebook || '',
          instagram: bizRes.data.instagram || '',
          twitter: bizRes.data.twitter || '',
          ownership_tags: bizRes.data.ownership_tags || [],
          hours: bizRes.data.hours || {}
        });
        // Load subcategories for existing category
        if (existingCat) {
          const subRes = await categoriesApi.getBusinessSubcategories(existingCat);
          setSubcategories(subRes.data);
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleCategoryChange = async (newCategory) => {
    setForm({ ...form, primary_category: newCategory, subcategory: '' });
    if (newCategory) {
      try {
        const subRes = await categoriesApi.getBusinessSubcategories(newCategory);
        setSubcategories(subRes.data);
      } catch (error) {
        setSubcategories([]);
      }
    } else {
      setSubcategories([]);
    }
  };

  const handlePhotoChange = (e) => {
    const files = Array.from(e.target.files);
    if (photos.length + files.length > 5) {
      toast.error('Maximum 5 photos allowed');
      return;
    }
    
    setPhotos([...photos, ...files]);
    
    // Create previews
    const newPreviews = files.map(file => URL.createObjectURL(file));
    setPhotosPreviews([...photosPreviews, ...newPreviews]);
  };

  const removePhoto = (index) => {
    const newPhotos = [...photos];
    newPhotos.splice(index, 1);
    setPhotos(newPhotos);
    
    const newPreviews = [...photosPreviews];
    URL.revokeObjectURL(newPreviews[index]);
    newPreviews.splice(index, 1);
    setPhotosPreviews(newPreviews);
  };

  const toggleOwnershipTag = (tag) => {
    if (form.ownership_tags.includes(tag)) {
      setForm({ ...form, ownership_tags: form.ownership_tags.filter(t => t !== tag) });
    } else {
      setForm({ ...form, ownership_tags: [...form.ownership_tags, tag] });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (form.description.length > 300) {
      toast.error('Description must be 300 characters or less');
      return;
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('business_name', form.business_name);
      formData.append('primary_category', form.primary_category);
      formData.append('description', form.description);
      formData.append('street_address', form.street_address);
      formData.append('city_state_zip', form.city_state_zip);
      
      if (form.secondary_category) formData.append('secondary_category', form.secondary_category);
      if (form.subcategory) formData.append('subcategory', form.subcategory);
      if (form.phone) formData.append('phone', form.phone);
      if (form.website) formData.append('website', form.website);
      if (form.contact_email) formData.append('contact_email', form.contact_email);
      if (form.facebook) formData.append('facebook', form.facebook);
      if (form.instagram) formData.append('instagram', form.instagram);
      if (form.twitter) formData.append('twitter', form.twitter);
      
      formData.append('ownership_tags', JSON.stringify(form.ownership_tags));
      formData.append('hours', JSON.stringify(form.hours));
      
      photos.forEach(photo => {
        formData.append('photos', photo);
      });
      
      if (existingBusiness) {
        await businessApi.update(existingBusiness.id, formData);
        toast.success('Business updated successfully!');
      } else {
        await businessApi.create(formData);
        toast.success('Business submitted for approval!');
      }
      
      navigate('/business-account');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit business');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 py-8" data-testid="submit-business-page">
      <Helmet>
        <title>Submit Your Business | Free Listing in Martinsville & Henry County, VA | Explore MHC</title>
        <meta name="description" content="Add your business to the Martinsville and Henry County directory for free. Get discovered by the local community on Explore MHC." />
      </Helmet>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-neutral-900 mb-2" style={{ fontFamily: 'DM Serif Display, serif' }}>
            {existingBusiness?.status === 'rejected' ? 'Resubmit Your Business' : existingBusiness ? 'Edit Your Business' : 'Submit Your Business'}
          </h1>
          <p className="text-neutral-600">
            {existingBusiness?.status === 'rejected' ? 'Make changes and resubmit for approval' : existingBusiness ? 'Update your business listing information' : 'Add your business to the Explore MHC directory'}
          </p>
          {!existingBusiness && (
            <p className="text-sm text-neutral-500 mt-2">
              Free listing included. Want extra visibility? <Link to="/pricing" className="text-primary font-medium hover:underline">See featured placement options</Link> starting at $10/week.
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          {/* Basic Info */}
          <div className="form-section">
            <h2 className="form-section-title">Basic Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="form-label">Business Name *</label>
                <input
                  type="text"
                  required
                  value={form.business_name}
                  onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                  className="form-input"
                  data-testid="business-name-input"
                />
              </div>
              
              <div>
                <label className="form-label">Primary Category *</label>
                <select
                  required
                  value={form.primary_category}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  className="form-input"
                  data-testid="primary-category-select"
                >
                  <option value="">Select a category</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="form-label">Subcategory{subcategories.length > 0 ? ' *' : ''}</label>
                <select
                  required={subcategories.length > 0}
                  value={form.subcategory}
                  onChange={(e) => setForm({ ...form, subcategory: e.target.value })}
                  className="form-input"
                  disabled={!form.primary_category || subcategories.length === 0}
                  data-testid="subcategory-select"
                >
                  <option value="">{form.primary_category ? 'Select a subcategory' : 'Select a category first'}</option>
                  {subcategories.map(sub => (
                    <option key={sub} value={sub}>{sub}</option>
                  ))}
                </select>
              </div>
              
              <div className="md:col-span-2">
                <label className="form-label">Description * ({form.description.length}/300 characters)</label>
                <textarea
                  required
                  maxLength={300}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="form-textarea"
                  rows={3}
                  data-testid="description-textarea"
                />
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="form-section">
            <h2 className="form-section-title">Location</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="form-label">Street Address <span className="text-neutral-400 font-normal text-sm">(optional — leave blank for home-based businesses)</span></label>
                <input
                  type="text"
                  value={form.street_address}
                  onChange={(e) => setForm({ ...form, street_address: e.target.value })}
                  className="form-input"
                  data-testid="street-address-input"
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="form-label">City, State ZIP *</label>
                <input
                  type="text"
                  required
                  value={form.city_state_zip}
                  onChange={(e) => setForm({ ...form, city_state_zip: e.target.value })}
                  className="form-input"
                  placeholder="Martinsville, VA 24112"
                  data-testid="city-state-zip-input"
                />
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="form-section">
            <h2 className="form-section-title">Contact Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="form-label">Phone Number</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="form-input"
                />
              </div>
              
              <div>
                <label className="form-label">Website URL</label>
                <input
                  type="url"
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                  className="form-input"
                  placeholder="https://example.com"
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="form-label">Contact Email (not shown publicly)</label>
                <input
                  type="email"
                  value={form.contact_email}
                  onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                  className="form-input"
                />
              </div>
            </div>
          </div>

          {/* Social Media */}
          <div className="form-section">
            <h2 className="form-section-title">Social Media</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="form-label">Facebook URL</label>
                <input
                  type="url"
                  value={form.facebook}
                  onChange={(e) => setForm({ ...form, facebook: e.target.value })}
                  className="form-input"
                />
              </div>
              
              <div>
                <label className="form-label">Instagram URL</label>
                <input
                  type="url"
                  value={form.instagram}
                  onChange={(e) => setForm({ ...form, instagram: e.target.value })}
                  className="form-input"
                />
              </div>
              
              <div>
                <label className="form-label">X (Twitter) URL</label>
                <input
                  type="url"
                  value={form.twitter}
                  onChange={(e) => setForm({ ...form, twitter: e.target.value })}
                  className="form-input"
                />
              </div>
            </div>
          </div>

          {/* Photos */}
          <div className="form-section">
            <h2 className="form-section-title">Photos (up to 5)</h2>
            
            <div className="photo-upload-grid">
              {photosPreviews.map((preview, index) => (
                <div key={index} className="photo-upload-slot relative">
                  <img src={preview} alt="" />
                  <button
                    type="button"
                    onClick={() => removePhoto(index)}
                    className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {photos.length < 5 && (
                <label className="photo-upload-slot cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoChange}
                    className="hidden"
                  />
                  <div className="text-center">
                    <Upload className="w-8 h-8 text-neutral-400 mx-auto mb-2" />
                    <span className="text-sm text-neutral-500">Add Photo</span>
                  </div>
                </label>
              )}
            </div>
          </div>

          {/* Ownership Tags */}
          <div className="form-section">
            <h2 className="form-section-title">Ownership Identity (Optional)</h2>
            <p className="text-sm text-neutral-600 mb-4">Select any that apply to your business</p>
            
            <div className="checkbox-group">
              {ownershipTags.map(tag => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => toggleOwnershipTag(tag)}
                  className={`checkbox-item ${form.ownership_tags.includes(tag) ? 'selected' : ''}`}
                >
                  <span className={`w-5 h-5 rounded border flex items-center justify-center ${
                    form.ownership_tags.includes(tag) ? 'bg-primary border-primary text-white' : 'border-neutral-300'
                  }`}>
                    {form.ownership_tags.includes(tag) && '✓'}
                  </span>
                  <span className="text-sm">{tag}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Hours */}
          <div className="form-section">
            <h2 className="form-section-title">Hours of Operation (Optional)</h2>
            
            <div className="space-y-4">
              {DAYS.map(day => (
                <div key={day} className="flex items-center gap-4">
                  <span className="w-24 font-medium capitalize">{day}</span>
                  <input
                    type="text"
                    value={form.hours[day] || ''}
                    onChange={(e) => setForm({ 
                      ...form, 
                      hours: { ...form.hours, [day]: e.target.value } 
                    })}
                    className="form-input flex-1"
                    placeholder="9:00 AM - 5:00 PM or Closed"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Featured Placement */}
          <div className="form-section">
            <h2 className="form-section-title">Featured Placement (Optional)</h2>
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-5">
              <div className="flex items-start gap-3 mb-3">
                <Star className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-neutral-900">Want your business to stand out?</h3>
                  <p className="text-sm text-neutral-600 mt-1">
                    Featured businesses appear at the top of directory listings, on the homepage, and get priority placement across the site.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                <div className="bg-white rounded-lg border border-amber-200 p-4">
                  <p className="font-bold text-neutral-900">$10<span className="text-sm font-normal text-neutral-500">/week</span></p>
                  <p className="text-xs text-neutral-500">Featured Business Weekly</p>
                </div>
                <div className="bg-white rounded-lg border border-amber-200 p-4">
                  <p className="font-bold text-neutral-900">$30<span className="text-sm font-normal text-neutral-500">/month</span></p>
                  <p className="text-xs text-neutral-500">Featured Business Monthly</p>
                </div>
              </div>
              <p className="text-xs text-neutral-500 mt-3">
                Submit your business first for approval. Once approved, you can purchase featured placement from your dashboard.
              </p>
            </div>
          </div>

          {/* Submit */}
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => navigate('/business-account')}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary flex-1"
              disabled={loading}
              data-testid="submit-business-btn"
            >
              {loading ? 'Submitting...' : existingBusiness?.status === 'rejected' ? 'Resubmit for Approval' : existingBusiness ? 'Update Business' : 'Submit for Approval'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SubmitBusiness;
